<template>
    <KitPendingNotice v-if="showKitNotice" :product="type === 'ydna' ? 'Fatherline' : 'Motherline'" />
    <div v-else class="container d-flex flex-column align-items-center justify-content-center">
        <div class="col-md-10 col-lg-9 col-xl-8 m-5 mx-auto">
            <div class="card mb-3 rounded-4 overflow-hidden">
                <div class="row g-0">
                    <div class="col-sm-5 d-flex align-items-end position-relative overflow-hidden">
                        <div class="bg-gradient-circle position-absolute z-0 w-75 h-100"></div>
                        <img
                            :src="errorMessage.title === 'Upgrade required' 
                                    ? UpgradeLineIcon 
                                    : (type === 'ydna' ? FatherLineIcon : MotherLineIcon)"
                            class="img-fluid mt-5 mx-auto z-1"
                            alt="Health"
                            />
                        <!-- <img :src="(type == 'ydna') ? FatherLineIcon : MotherLineIcon " class="img-fluid mt-5 mx-auto z-1"
                            alt="Health"> -->
                    </div>
                    <div class="col-sm-7">
                        <div
                            class="card-body d-flex flex-column align-items-start justify-content-center h-100 p-4">
                            <h5 class="card-title text-start my-3" v-if="errorMessage.title">{{ errorMessage.title }}</h5>
                            <!-- <p class="card-text text-start">{{ details.heading }}</p> -->
                            <p class="card-text text-start">
                                <small class="text-muted" v-html="errorMessage.description"></small>
                            </p>
                            <router-link v-if="isEpigenetics" :to="{ name: 'HealthStore', query: { tab: 'ancestrykits' } }"
                                class="btn btn-primary">Explore ancestry kits</router-link>
                            <router-link v-else-if="errorMessage.link" :to="{ path: errorMessage.link }">
                                <button class="btn btn-primary">Explore</button>
                            </router-link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.bg-gradient-circle {
    background: url('@/assets/gradient-circle.png') no-repeat;
    background-position: right;
    // background-size: cover;
}

img {
    max-height: 330px;
}
</style>

<script setup>
import "@/styles/components/ParentalPageError.scss";
import MotherLineIcon from "@/assets/characters/Motherline_upgrade.png";
import FatherLineIcon from "@/assets/characters/fatherline.png";
import UpgradeLineIcon from "@/assets/characters/upgrade-required-image.png";
import store from "@/store/index";
import { computed, defineProps } from "vue";
import KitPendingNotice from "@/components/kit/KitPendingNotice.vue";
import { useAncestryKit } from "@/composables/useAncestryKit";

const props = defineProps({
    type: String,
});

const isEpigenetics = computed(() => store.getters.profile?.profile?.barcode?.endsWith('E') === true);

// A kit on its way to results that includes this line: where it's up to. Fatherline still
// explains itself to genetic females, who won't get one.
const { pending: kitPending } = useAncestryKit();
const showKitNotice = computed(() =>
    kitPending.value &&
    store.getters.kitTests?.[props.type]?.purchased === true &&
    !(props.type === "ydna" && store.getters.profile?.profile?.gender === 2)
);
const test = store.getters.kitTests?.[props.type];
const isUploader = store.getters.profile?.profile?.barcode?.slice(0, 2) === "LT";
const updateTest = isUploader ? 'member' : 'deep';
const statusDescriptions = {
    cancelled: {
        type: "error",
        title: "Your test has been cancelled",
        description: "Looks like you don't have an ancestry test.",
    },
    genotyping: {
        type: "info",
        title: "Your test is currently processing",
        description:
            "Your ancestry results are processing. You will receive an email when your results are ready.",
    },
    returned: {
        type: "info",
        title: "Your test is currently processing",
        description:
            "Your ancestry results are processing. You will receive an email when your results are ready.",
    },
    shipped: {
        type: "info",
        title: "Your test is currently processing",
        description:
            "Your ancestry information results are processing. You will receive an email when your results are ready.",
    },
    testing: {
        type: "info",
        title: "Your test is currently processing",
        description:
            "Your ancestry results are processing. You will receive an email when your results are ready.",
    },
    testing_failed: {
        type: "error",
        title: "Your test has failed",
        description:
            "We have encountered an issue in processing your DNA. Please check your email for instruction.",
    },
    no_data: {
        uploader: {
            type: "warning",
            title: "Data unavailable",
            description: "You do not have the data to provide results for this test.",
        },
        starter_kit: {
            type: "info",
            title: "Upgrade required",
            description:
                "Unlock the furthest-reaching history of your ancestors with the "+updateTest+" ancestry upgrade. See migration routes, your haplogroup, and where your DNA signature is commonly found today.",
            link: ""//store/Deep-Ancestry-Upgrade,
        },
    },
};
    
const errorMessage = computed(() => {
    if (isEpigenetics.value) {
        return {
            title: "Requires an ancestry kit",
            description: "This feature is only available for ancestry kits.",
            link: null,
        };
    }
    if (props.type === "ydna" && store.getters.profile.profile.gender === 2) {
        return {
            title: "Y DNA result not available",
            description: `
              <p>Due to the way the Y Chromosome is passed down, from Male to Male, we are able to provide fatherline Y-DNA haplogroup to anyone who takes our test and is genetically male.</p>
              <p>Genetic females do not carry a Y Chromosome and therefore will not receive their YDNA haplogroup. To discover this information you could consider testing a direct male descendent such as a full brother or paternal uncle.</p>
          `,
            type: "info",
        };
    }
    const message = statusDescriptions[test.slug];
    const processingStatuses = ["genotyping", "returned", "shipped", "testing"];
    if (isUploader && processingStatuses.includes(test.slug)) {
        return statusDescriptions.no_data.uploader;
    }
    if (message?.title) {
        return message;
    }
    if (test?.slug === "no_data") {
        const isUploader = store.getters.profile?.profile?.barcode?.slice(0, 2) === "LT";
        return isUploader ? message.uploader : message.starter_kit;
    }
    return {
        title: "Failed to load data",
        description: "We have encountered an issue in loading your result",
        type: "error",
    };
});
</script>